import React, { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading, token } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  const loadingStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #6b21a8, #7c3aed, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const loadingCardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    padding: '2rem'
  };

  const loadingContentStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const spinnerStyle: React.CSSProperties = {
    animation: 'spin 1s linear infinite',
    borderRadius: '50%',
    height: '2rem',
    width: '2rem',
    borderBottom: '2px solid #9333ea',
    marginRight: '0.75rem'
  };

  const loadingTextStyle: React.CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: '500',
    color: '#1f2937'
  };

  // Evitar loop: limpar token inválido apenas uma vez quando detectado
  useEffect(() => {
    if (!isLoading && !token && !user) {
      // Limpar qualquer token inválido (apenas se ainda existir no localStorage)
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('default_company_id');
      }
    }
  }, [isLoading, token, user]);

  if (isLoading) {
    return (
      <div style={loadingStyle}>
        <div style={loadingCardStyle}>
          <div style={loadingContentStyle}>
            <div style={spinnerStyle}></div>
            <span style={loadingTextStyle}>{t('common.loading')}</span>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Se não tem usuário nem token, redirecionar para login usando Navigate
  if (!user || !token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

